<script setup lang="ts">
  import { EnvelopeIcon, ShieldCheckIcon } from '@heroicons/vue/24/solid'

  definePageMeta({
    layout: 'centered'
  })

  const { addErrorNotification } = useNotifications()
  const { auth } = useSupabaseClient()
  const router = useRouter()

  // Variables for the form
  const code = router.currentRoute.value.query.code as string
  const resetType = ref<'define-password' | 'need-email'>(code ? 'define-password' : 'need-email')
  const errorMessage = ref<string | null>(null)
  const isLoading = ref<boolean>(false)

  // Variables for the 'need-email' form
  const email = ref<string | null>(null)
  const isEmailSend = ref<boolean>(false)

  // Variables for the 'define-password' form
  const password = ref<string | null>('')
  const passwordConfirm = ref<string | null>('')
  const isPasswordReset = ref<boolean>(false)
  
  // Extracting the data from the URL hash (if any, in case of new password action sent from Supabase admin panel)
  let routeHash: any = router.currentRoute.value.hash
  if (routeHash.indexOf('#access_token=') === 0) {
    try {
      routeHash = routeHash.replace('#', '')
      routeHash = JSON.parse(`{"${(routeHash.replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"'))}"}`)
    } catch (e) {
      console.error(e)
      addErrorNotification({
        message: 'Le token de réinitialisation n\'a pas pu être récupéré'
      })
    }
    resetType.value = routeHash.access_token && routeHash.type === 'recovery' ? 'define-password' : 'need-email'
  }

  async function submitForm() {
    // Better UI
    isLoading.value = true
    errorMessage.value = null

    if (resetType.value === 'need-email') {

      // Validating the data
      if (!email.value) {
        errorMessage.value = 'Veuillez entrer une adresse e-mail'
        isLoading.value = false
        return
      }

      // Sending the email for password reset
      const { error } = await auth.resetPasswordForEmail(email.value,
        { redirectTo: `${window.location.origin}/password-reset` }
      )

      // Handling the error
      if (error) {
        console.log(error)
        errorMessage.value = error.message || 'Une erreur inconnue est survenue'
        isLoading.value = false
        return
      }

      isEmailSend.value = true

    } else if (resetType.value === 'define-password') {

      // Validating the data
      if (!password.value) {
        errorMessage.value = 'Veuillez entrer un mot de passe'
      } else if (password.value.length <= 5) {
        errorMessage.value = 'Le mot de passe doit contenir au moins 6 caractères'
      } else if (password.value !==  passwordConfirm.value) {
        errorMessage.value = 'Les mots de passe ne correspondent pas'
      }
      if (errorMessage.value !== null) {
        isLoading.value = false
        return
      }

      // Sending the new password to the database
      const { error } = await auth.updateUser({
        password: password.value ?? undefined
      })

      // Handling the error
      if (error) {
        errorMessage.value = error.message || 'Une erreur inconnue est survenue'
        isLoading.value = false
        return
      }

      isPasswordReset.value = true

    } else {
      console.error(new Error(`L'étape de réinitialisation de mot de passe n'est pas reconnue`))
      addErrorNotification({
        message: `L'étape de réinitialisation de mot de passe n'est pas reconnue`
      })
    }

    // Better UI
    isLoading.value = false
  }
</script>

<template>
  <div>
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <Logo class="mx-auto h-12 w-auto" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">Mot de passe oublié</h2>
      <p class="mt-2 text-center text-sm text-gray-600">
        Ou {{ ' ' }}        
        <NuxtLink href="/login" class="font-medium text-sky-blue-600 hover:text-sky-blue-500">
          se connecter à AppTrident
        </NuxtLink>
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">

        <template v-if="resetType === 'need-email'">

          <form v-if="!isEmailSend" class="space-y-6" @submit.prevent="submitForm">
            
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700"> Adresse email </label>
              <div class="mt-1">
                <input v-model="email" id="email" name="email" type="email" autocomplete="email" required class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-sky-blue-500 focus:border-sky-blue-500 sm:text-sm" />
              </div>              
              <p class="mt-1 text-xs text-gray-500">Même adresse email que votre achat de la Méthode Trident.</p>
            </div>

            <div>
              <Button :isLoading="isLoading" type="submit" class="w-full flex justify-center">
                M'envoyer un lien de réinitialisation
              </Button>
              <p :class="errorMessage ? 'h-[20px]' : 'h-0'" class="transition-all duration-300 mt-2 text-sm text-red-600" id="form-error">{{ errorMessage }}</p>
            </div>

          </form>

          <div v-else class="text-center">
            <EnvelopeIcon class="text-sky-blue-600 w-16 mx-auto" />
            <h2 class="mt-3 text-xl text-gray-700">
              Email de réinitialisation envoyé
            </h2>
            <p class="text-base text-gray-600 mt-3 px-10">
              Si un compte avec cette adresse existe, alors il vous suffit de cliquer sur le lien de réinitailisation dans l'email que vous venez de recevoir.
            </p>
          </div>

        </template>

        <template v-if="resetType === 'define-password'">

          <form v-if="!isPasswordReset" class="space-y-6" @submit.prevent="submitForm">

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700"> Nouveau mot de passe </label>
              <div class="mt-1">
                <input v-model="password" id="password" name="password" type="password" autocomplete="new-password" required class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-sky-blue-500 focus:border-sky-blue-500 sm:text-sm" />
              </div>
            </div>

            <div>
              <label for="passwordConfirm" class="block text-sm font-medium text-gray-700"> Confirmation du nouveau mot de passe </label>
              <div class="mt-1">
                <input v-model="passwordConfirm" id="passwordConfirm" name="passwordConfirm" type="password" autocomplete="new-password" required class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-sky-blue-500 focus:border-sky-blue-500 sm:text-sm" />
              </div>
            </div>

            <div>
              <Button :isLoading="isLoading" type="submit" class="w-full flex justify-center">
                Confirmer ce mot de passe
              </Button>
              <p :class="errorMessage ? 'h-[20px]' : 'h-0'" class="transition-all duration-300 mt-2 text-sm text-red-600" id="form-error">{{ errorMessage }}</p>
            </div>

          </form>

          <div v-else class="text-center">
            <ShieldCheckIcon class="text-sky-blue-600 w-16 mx-auto" />
            <h2 class="mt-3 text-xl text-gray-700">
              Mot de passe bien redéfini !
            </h2>
            <p class="text-base text-gray-600 mt-3 px-10">
              <NuxtLink to="/login" class="font-medium text-sky-blue-600 hover:text-sky-blue-500">Connectez-vous</NuxtLink> dès maintenant avec votre nouveau mot de passe.
            </p>
          </div>

        </template>
      </div>      
    </div>

  </div>
</template>
